// ===================================================================
// KAIXO - CRMAds
// ===================================================================
// Vista 'ads' del CRM: gestión de anuncios pagados (boosts) del
// usuario + publicaciones destacadas orgánicas. Permite pausar,
// reanudar y cancelar boosts desde aquí.
// ===================================================================

import React from 'react';
import Sparkline from '../Sparkline/Sparkline';
import ct from './i18n';

const euros = (cents) => `€${((cents || 0) / 100).toFixed(2)}`;

function BoostCard({ boost: b, statsForBoost, onAction }) {
    const pct = b.budgetCents > 0
        ? Math.min(100, (b.budgetSpentCents / b.budgetCents) * 100)
        : 0;
    const startDate = b.startsAt ? new Date(b.startsAt) : null;
    const endDate = b.endsAt ? new Date(b.endsAt) : null;
    const daysLeft = endDate
        ? Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)))
        : 0;
    const effectiveCpm = b.impressionsDelivered > 0
        ? (b.budgetSpentCents / b.impressionsDelivered) * 1000 / 100
        : 0;
    const cpe = b.impressionsDelivered > 0
        ? (b.budgetSpentCents / b.impressionsDelivered) / 100
        : 0;

    return (
        <div className="crm-ad-item">
            <div className="crm-ad-row">
                <div className="crm-ad-channel">
                    {b.channel?.avatar && <img src={b.channel.avatar} alt="" />}
                    <strong>{b.channel?.name || '—'}</strong>
                </div>
                <span className={`crm-ad-status crm-ad-status-${b.status}`}>{b.status}</span>
            </div>
            <div className="crm-ad-preview">
                {b.message?.mediaType === 'image' && b.message.mediaUrl && (
                    <img src={b.message.mediaUrl} alt="" />
                )}
                <p>{(b.message?.originalText || '').slice(0, 100)}</p>
            </div>
            <div className="crm-ad-kpis">
                <div><strong>{(b.impressionsDelivered || 0).toLocaleString()}</strong> <span>{ct.adImpressions}</span></div>
                <div><strong>{euros(b.budgetSpentCents)}</strong> <span>{ct.adSpent}</span></div>
                <div><strong>{euros(b.budgetCents)}</strong> <span>{ct.adBudget}</span></div>
                <div><strong>{((b.ctr || 0) * 100).toFixed(2)}%</strong> <span>{ct.adCtr}</span></div>
            </div>
            <div className="crm-ad-kpis">
                <div><strong>€{effectiveCpm.toFixed(2)}</strong> <span>CPM real</span></div>
                <div><strong>€{cpe.toFixed(4)}</strong> <span>CPE</span></div>
                <div><strong>{daysLeft}</strong> <span>días restantes</span></div>
                <div><strong>{b.cpmTier || '—'}</strong> <span>Tier</span></div>
            </div>
            <div className="crm-ad-progress">
                <div className="crm-ad-progress-bar" style={{ width: `${pct}%` }} />
            </div>
            {statsForBoost?.length > 0 && (
                <div className="crm-ad-sparkline">
                    <span className="crm-ad-sparkline-label">Últimos 7 días</span>
                    <Sparkline data={statsForBoost} width={160} height={28} />
                </div>
            )}

            <details className="crm-ad-details">
                <summary>Configuración y audiencia</summary>
                <div className="crm-ad-details-grid">
                    <div><span>Inicio:</span> <strong>{startDate ? startDate.toLocaleDateString() : '—'}</strong></div>
                    <div><span>Fin:</span> <strong>{endDate ? endDate.toLocaleDateString() : '—'}</strong></div>
                    <div><span>CPM base:</span> <strong>€{((b.cpmCents || 0) / 100).toFixed(2)}</strong></div>
                    <div><span>Likes:</span> <strong>{b.likes || 0}</strong></div>
                    <div><span>Comentarios:</span> <strong>{b.comments || 0}</strong></div>
                    <div><span>Compartidos:</span> <strong>{b.shares || 0}</strong></div>
                </div>
                {(b.targetCountries?.length > 0 || b.targetRegions?.length > 0 || b.targetLanguages?.length > 0 || b.targetInterests?.length > 0) && (
                    <div className="crm-ad-targeting">
                        {b.targetCountries?.length > 0 && <div><span>Países:</span> {b.targetCountries.join(', ')}</div>}
                        {b.targetRegions?.length > 0 && <div><span>Regiones:</span> {b.targetRegions.join(', ')}</div>}
                        {b.targetLanguages?.length > 0 && <div><span>Idiomas:</span> {b.targetLanguages.join(', ')}</div>}
                        {b.targetInterests?.length > 0 && <div><span>Intereses:</span> {b.targetInterests.join(', ')}</div>}
                    </div>
                )}
            </details>

            <div className="crm-ad-actions">
                {b.status === 'active' && (
                    <button className="crm-btn-secondary-sm" onClick={() => onAction(b._id, 'pause')}>Pausar</button>
                )}
                {b.status === 'paused' && (
                    <button className="crm-btn-secondary-sm" onClick={() => onAction(b._id, 'resume')}>Reanudar</button>
                )}
                {!['done', 'cancelled'].includes(b.status) && (
                    <button className="crm-btn-danger-sm" onClick={() => onAction(b._id, 'cancel')}>Cancelar</button>
                )}
            </div>
        </div>
    );
}

export default function CRMAds({ boosts, boostStatsMap, bestAds, onBoostAction }) {
    return (
        <>
            <div className="crm-ads-hint">
                {ct.createAd || 'Para promocionar un latido, ve al canal y pulsa el botón "Impulsar" en el post.'}
            </div>
            <h4 style={{ margin: '6px 0 8px' }}>💰 Anuncios pagados</h4>
            {boosts.length === 0 ? (
                <p className="crm-center crm-dim">{ct.noAds}</p>
            ) : boosts.map(b => (
                <BoostCard
                    key={b._id}
                    boost={b}
                    statsForBoost={boostStatsMap?.[b._id]}
                    onAction={onBoostAction}
                />
            ))}

            {bestAds.length > 0 && (
                <div className="crm-best-ads" style={{ marginTop: 14 }}>
                    <h4 style={{ margin: '0 0 8px' }}>🔥 Publicaciones destacadas (orgánicas)</h4>
                    <p className="crm-dim" style={{ fontSize: 12, margin: '0 0 10px' }}>
                        Tus publicaciones con más interacción. No están impulsadas pero tienen buen rendimiento — podrías promocionarlas.
                    </p>
                    {bestAds.slice(0, 5).map(ad => (
                        <div key={ad._id} className="crm-best-ad">
                            <div className="crm-best-ad-head">
                                {ad.mediaType === 'image' && ad.mediaUrl
                                    ? <img src={ad.mediaUrl} alt="" className="crm-best-ad-thumb" />
                                    : <div className="crm-best-ad-thumb crm-best-ad-thumb-text">📝</div>}
                                <div className="crm-best-ad-body">
                                    <strong>{ad.channelName}</strong>
                                    <p>{ad.text || '—'}</p>
                                </div>
                            </div>
                            <div className="crm-best-ad-stats">
                                <span>❤️ {ad.reactions}</span>
                                <span>💬 {ad.commentsCount}</span>
                                <span>📤 {ad.sharesCount}</span>
                                <span className="crm-best-ad-score">Score: {ad.interactionScore}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
